import * as React from 'react'
import { BarChart3, RefreshCw, ShieldCheck } from 'lucide-react'
import { useReleaseAnalytics } from '@/lib/queries'
import type { ReleaseAnalyticsGroup, ReleaseCycle } from '@/lib/types'
import { PageHeader } from '@/components/layout/AppShell'
import { DomainDetailDrawer } from '@/components/layout/DomainDetailDrawer'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { formatDateTime, formatNumber } from '@/lib/utils'

const ALL = '__all__'
const BEIJING = 'Asia/Shanghai'

export default function ReleaseAnalyticsPage() {
  const [tld, setTld] = React.useState('')
  const [registrar, setRegistrar] = React.useState('')
  const [from, setFrom] = React.useState('')
  const [to, setTo] = React.useState('')
  const [detailId, setDetailId] = React.useState<string | null>(null)
  const [groupFor, setGroupFor] = React.useState<ReleaseAnalyticsGroup | null>(null)
  const query = useReleaseAnalytics({
    tld: tld || undefined,
    registrar: registrar || undefined,
    from: from || undefined,
    to: to || undefined,
    scope: 'observed',
    limit: 100,
  })

  if (query.error) {
    return (
      <>
        <PageHeader title="释放统计" />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </>
    )
  }

  const data = query.data
  const summary = data?.summary

  return (
    <>
      <PageHeader
        title="释放统计"
        description="按到期时的注册商和后缀，统计确认可注册或已重新注册的周期。"
        actions={
          <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        }
      />

      <Card className="mb-5">
        <CardContent className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-5">
          <FilterSelect
            label="后缀"
            value={tld}
            placeholder="全部后缀"
            items={data?.filters.tlds ?? []}
            renderItem={(value) => `.${value}`}
            onChange={setTld}
          />
          <FilterSelect
            label="到期时注册商"
            value={registrar}
            placeholder="全部注册商"
            items={data?.filters.registrars ?? []}
            onChange={setRegistrar}
          />
          <DateFilter label="到期日期从" value={from} onChange={setFrom} />
          <DateFilter label="到期日期至" value={to} onChange={setTo} />
          <div className="flex items-end">
            <Button
              variant="ghost"
              className="w-full"
              disabled={!tld && !registrar && !from && !to}
              onClick={() => { setTld(''); setRegistrar(''); setFrom(''); setTo('') }}
            >
              清除筛选
            </Button>
          </div>
        </CardContent>
      </Card>

      {query.isLoading || !data || !summary ? (
        <LoadingState rows={7} label="正在汇总释放周期" />
      ) : (
        <>
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{data.notice} 所有时间按北京时间展示。</span>
          </div>

          <Card className="mb-5">
            <CardHeader>
              <CardTitle>注册商 × 后缀</CardTitle>
              <CardDescription>注册周期已重置的域名按“已释放后重新注册”纳入汇总；没抓到开放时刻时，耗时按重新注册时间计算上界。</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              {data.groups.length === 0 ? (
                <div className="p-6"><EmptyState icon={BarChart3} title="暂无分组数据" description="确认可注册或发现注册周期已重置后，会按到期时注册商和后缀自动归组。" /></div>
              ) : (
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className="border-y bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                      <Th>到期时注册商</Th><Th>后缀</Th><Th>样本</Th><Th>典型周期</Th><Th>常见周期（中间 50%）</Th><Th>90% 以内</Th><Th>主要确认/重注时段（北京时间）</Th><Th>周期范围（含上界）</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.groups.map((group) => (
                      <tr key={`${group.tld}:${group.registrar}`}>
                        <Td className="max-w-[300px] font-medium">{group.registrar}</Td>
                        <Td>.{group.tld}</Td>
                        <Td>
                          <button
                            type="button"
                            className="rounded font-medium tabular-nums text-primary underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => setGroupFor(group)}
                            aria-label={`查看 ${group.registrar} .${group.tld} 的 ${formatNumber(group.samples)} 个样本构成`}
                          >
                            {formatNumber(group.samples)}
                          </button>
                        </Td>
                        <Td>{formatStatDays(group.median_delay_days)}</Td>
                        <Td>{formatStatDays(group.p25_delay_days)}–{formatStatDays(group.p75_delay_days)}</Td>
                        <Td>{formatStatDays(group.p90_delay_days)}</Td>
                        <Td>
                          {String(group.peak_release_hour).padStart(2, '0')}:00
                          <span className="ml-1 text-xs text-muted-foreground">({Math.round(group.peak_hour_percentage)}%)</span>
                        </Td>
                        <Td>{formatStatDays(group.min_delay_days)}–{formatStatDays(group.max_delay_days)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>周期明细</CardTitle>
              <CardDescription>保留所有结局，便于核对续费、漏检后重新注册和正式释放样本。</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              {data.cycles.length === 0 ? (
                <div className="p-6"><EmptyState title="暂无周期记录" description="有到期时间的域名会在后续查询中开始积累周期。" /></div>
              ) : (
                <table className="w-full min-w-[1050px] text-sm">
                  <thead className="border-y bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                      <Th>域名</Th><Th>到期时注册商</Th><Th>注册局到期时间</Th><Th>结局</Th><Th>确认/变更时间</Th><Th>周期耗时</Th><Th>观察精度</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.cycles.map((cycle) => (
                      <tr key={cycle.id}>
                        <Td>
                          <button className="font-mono text-primary hover:underline" onClick={() => setDetailId(cycle.domain_id)}>{cycle.domain_ascii}</button>
                          {cycle.is_demo && <Badge variant="muted" className="ml-1">演示</Badge>}
                        </Td>
                        <Td className="max-w-[260px]">{cycle.registrar_at_expiry ?? '未知'}</Td>
                        <Td>{formatDateTime(cycle.expires_at, BEIJING)}</Td>
                        <Td><OutcomeBadge outcome={cycle.outcome} /></Td>
                        <Td>{formatDateTime(cycleTime(cycle), BEIJING)}</Td>
                        <Td>{cycleDuration(cycle)}</Td>
                        <Td>{cycle.observation_window_seconds == null ? '—' : formatDuration(cycle.observation_window_seconds)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={groupFor !== null} onOpenChange={(open) => { if (!open) setGroupFor(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>样本构成</DialogTitle>
            <DialogDescription>
              {groupFor?.registrar} × .{groupFor?.tld}
            </DialogDescription>
          </DialogHeader>
          {groupFor && (
            <div className="space-y-4">
              <div className="divide-y rounded-lg border">
                <SampleCompositionRow
                  label="确认可注册"
                  description="已直接捕获可注册状态"
                  value={groupFor.confirmed_samples}
                />
                <SampleCompositionRow
                  label="已释放后重新注册"
                  description="注册周期已重置，开放时刻未被捕获"
                  value={groupFor.reregistered_without_available}
                />
              </div>
              <div className="rounded-lg bg-muted/50 px-4 py-3">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium">样本合计</span>
                  <span className="text-lg font-semibold tabular-nums">{formatNumber(groupFor.samples)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DomainDetailDrawer domainId={detailId} onClose={() => setDetailId(null)} />
    </>
  )
}

function SampleCompositionRow({ label, description, value }: {
  label: string
  description: string
  value: number
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <span className="shrink-0 font-semibold tabular-nums">{formatNumber(value)}</span>
    </div>
  )
}

function FilterSelect({ label, value, placeholder, items, onChange, renderItem = (v) => v }: {
  label: string; value: string; placeholder: string; items: string[]
  onChange: (value: string) => void; renderItem?: (value: string) => string
}) {
  return (
    <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <Select value={value || ALL} onValueChange={(next) => onChange(next === ALL ? '' : next)}>
        <SelectTrigger className="h-10 text-foreground"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{placeholder}</SelectItem>
          {items.map((item) => <SelectItem key={item} value={item}>{renderItem(item)}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  )
}

function DateFilter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="space-y-1.5 text-xs font-medium text-muted-foreground"><span>{label}</span><Input className="h-10 text-foreground" type="date" value={value} onChange={(event) => onChange(event.target.value)} /></label>
}

function Th({ children }: { children: React.ReactNode }) { return <th className="whitespace-nowrap px-4 py-2.5 font-medium">{children}</th> }
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td> }

function OutcomeBadge({ outcome }: { outcome: ReleaseCycle['outcome'] }) {
  const config = {
    tracking: ['跟踪中', 'muted'], renewal_candidate: ['待确认续费', 'warning'], renewed: ['已续费', 'outline'],
    available_confirmed: ['已确认释放', 'success'], reregistered_without_availability: ['已重新注册（未捕获开放）', 'warning'],
    available_then_registered: ['确认释放后已注册', 'success'], unresolved: ['未确定', 'muted'],
  } as const
  const [label, variant] = config[outcome]
  return <Badge variant={variant}>{label}</Badge>
}

function cycleTime(cycle: ReleaseCycle): string | undefined {
  return cycle.first_available_at ?? cycle.re_registered_at ?? cycle.renewed_at ?? cycle.renewal_candidate_at
}

function cycleDuration(cycle: ReleaseCycle): string {
  if (cycle.release_delay_seconds != null) return formatDuration(cycle.release_delay_seconds)
  if (cycle.outcome === 'renewal_candidate' && cycle.renewal_confirm_after) {
    return `观察至 ${formatDateTime(cycle.renewal_confirm_after, BEIJING)}`
  }
  if (cycle.outcome === 'reregistered_without_availability' && cycle.re_registered_at) {
    const seconds = Math.max(0, (Date.parse(cycle.re_registered_at) - Date.parse(cycle.expires_at)) / 1000)
    return `≤ ${formatDuration(seconds)}（上界）`
  }
  return '—'
}

function formatDays(value: number): string {
  const totalHours = Math.max(0, Math.round(value * 24))
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  if (days === 0) return `${hours} 小时`
  if (hours === 0) return `${days} 天`
  return `${days} 天 ${hours} 小时`
}

function formatStatDays(value: number): string {
  return `约 ${Math.max(0, Math.round(value))} 天`
}

function formatDuration(seconds: number): string {
  const days = seconds / 86400
  if (days >= 1) return formatDays(days)
  const hours = seconds / 3600
  if (hours >= 1) return `${hours.toLocaleString('zh-CN', { maximumFractionDigits: 1 })} 小时`
  return `${Math.max(0, Math.round(seconds / 60))} 分钟`
}
